import {
  GetAccount200ResponsePositionsInner,
  GetOrderbook200Response,
  SideResponse,
} from "../codegen-api";
import { calculatePnl } from "./math";
import { knownPrice } from "./unknownNumbers";

export type CloseLiquidityType =
  | "sufficient"
  | "blend"
  | "insufficient"
  | "none";

export interface PositionCloseEstimate {
  liquidityType: CloseLiquidityType;
  averagePrice?: number;
  /**
   * Whole-position outlook: realised on the part that closes PLUS unrealised on
   * the part that stays open. Useful, but it is NOT what closing costs — see
   * `realizedPnl`, which is what the modal's "Est. Close PnL" must show.
   */
  pnl?: number;
  /**
   * What this close actually realises: the fillable portion only, at the price
   * the book gives.
   *
   * Kept separate because blending the two flipped the sign on screen. A close
   * of a long entered at $84,000.12 against a book averaging $76,728.22 realises
   * a LOSS, but the remainder's unrealised gain outweighed it and the modal
   * showed "Est. Close PnL +$90.85" in green -- while its own confirmation copy,
   * built from the same estimate, said "Realized loss ... -$13.09". On a thin
   * book, which is the normal state here, the two disagreed on every close.
   */
  realizedPnl?: number;
  /** Entry notional of the portion that closes, so ROI has the right base. */
  closedEntryValue?: number;
  closedSize?: number;
  totalSize?: number;
  remainingSize?: number;
  remainingUnrealizedPnl?: number;
}

const num = (value: unknown) => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const estimateClosePositionFromOrderbook = (
  position?: GetAccount200ResponsePositionsInner,
  orderbook?: GetOrderbook200Response
): PositionCloseEstimate | undefined => {
  if (!position) return undefined;

  const totalSize = num(position.amount);
  const entryPrice = num(position.avg_entry_price);
  if (totalSize <= 0 || entryPrice <= 0) return undefined;

  const rawLevels =
    position.side === SideResponse.Buy ? orderbook?.bids : orderbook?.asks;
  if (!rawLevels?.length) {
    return { liquidityType: "none", totalSize };
  }

  const levels = [...rawLevels]
    .map(([price, size, iv]) => [num(price), num(size), iv] as [number, number, string | undefined])
    .filter(([price, size]) => price > 0 && size > 0)
    .sort(([a], [b]) =>
      position.side === SideResponse.Buy ? b - a : a - b
    );

  if (!levels.length) {
    return { liquidityType: "none", totalSize };
  }

  const totalLiquidity = levels.reduce((sum, [, size]) => sum + size, 0);
  const closedSize = Math.min(totalSize, totalLiquidity);
  if (closedSize <= 0) {
    return { liquidityType: "none", totalSize };
  }

  let remainingToFill = closedSize;
  let closingValue = 0;

  for (const [levelPrice, levelSize] of levels) {
    if (remainingToFill <= 0) break;
    const fillSize = Math.min(remainingToFill, levelSize);
    closingValue += fillSize * levelPrice;
    remainingToFill -= fillSize;
  }

  const averagePrice = closingValue / closedSize;
  const pnl = calculatePnl(
    entryPrice * closedSize,
    closingValue,
    position.side
  );
  const remainingSize = Math.max(0, totalSize - closedSize);
  // The part the book cannot absorb stays open at MARK. An unknown mark (null)
  // leaves that leg — and so the whole-position PnL — unknown; `num(null)` was
  // 0, which valued the remainder at $0 and invented a near-total loss.
  const mark = knownPrice(position.mark_price);
  const remainingUnknown = remainingSize > 0 && mark === undefined;
  const remainingUnrealizedPnl =
    remainingSize > 0 && mark !== undefined
      ? calculatePnl(
          entryPrice * remainingSize,
          mark * remainingSize,
          position.side
        )
      : undefined;

  return {
    liquidityType:
      levels[0][1] >= totalSize
        ? "sufficient"
        : totalLiquidity >= totalSize
        ? "blend"
        : "insufficient",
    averagePrice,
    // eslint-disable-next-line no-nested-ternary
    pnl: remainingUnknown
      ? undefined
      : remainingUnrealizedPnl !== undefined
      ? pnl + remainingUnrealizedPnl
      : pnl,
    realizedPnl: pnl,
    closedEntryValue: entryPrice * closedSize,
    closedSize,
    totalSize,
    remainingSize,
    remainingUnrealizedPnl,
  };
};
